import React, { useCallback } from 'react';
import { Asset, Group, GroupId, isCategorized } from 'hooks/useGrouping';

type PopupMenuBaseProps = {
  groups: Group[];
  currentGroupId: GroupId;
  showPopupMenu: boolean;
  closeHandlers: {
    onClose: () => void;
    shouldClosePopupMenuFromPoint: (event: React.MouseEvent) => boolean;
  };
  onPopupMenuItemClick: (groupId: GroupId) => Promise<void>;
  title: string;
  closeElement: JSX.Element;
  className?: string;
  backdropClickHandler?: (event: React.MouseEvent<HTMLDivElement>) => void;
};

const PopupMenuBase: React.FC<PopupMenuBaseProps> = props => {
  const filteredGroups = props.groups
    .filter(group => group.id.id !== props.currentGroupId.id)
    .sort((a, b) => {
      if (isCategorized(a)) {
        return isCategorized(b) ? 0 : 1;
      } else {
        return isCategorized(b) ? -1 : 0;
      }
    });

  const groupToItemMessage = (group: Group): string => {
    if (isCategorized(group)) {
      const name =
        group.name.length > 5 ? `${group.name.slice(0, 5)}…` : group.name;
      return `${name}に移動`;
    } else {
      return '未分類に戻す';
    }
  };
  const groupToItemIconClass = (group: Group): string => {
    if (isCategorized(group)) {
      return 'icon-corner-up-right';
    } else {
      return 'icon-rotate-ccw';
    }
  };

  const popupMenuItemClickHandler = (group: Group) => {
    return async (): Promise<void> => {
      await props.onPopupMenuItemClick(group.id);
    };
  };

  const calcTranslateY = (element: HTMLDivElement): number => {
    const popupMenuBottom =
      window.scrollY + element.getBoundingClientRect().bottom;
    const grouping = document.getElementsByClassName('grouping')[0];
    const containerBottom =
      window.scrollY + grouping.getBoundingClientRect().bottom;
    return Math.max(0, popupMenuBottom - containerBottom);
  };

  // HACK: bulkPopupMenuじゃなかったら、focusとblurで表示を制御する→上の階層に持っていくべき
  const popupMenuRef = useCallback((element: HTMLDivElement | null) => {
    if (!element) return;
    if (props.backdropClickHandler) return;
    element.style.transform = `translateY(-${calcTranslateY(element) + 5}px)`;
    element.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {props.showPopupMenu && (
        <>
          <div
            tabIndex={0}
            ref={popupMenuRef}
            onBlur={(): void => {
              props.closeHandlers.onClose();
            }}
            className={
              'popupmenu shadow-lg ' + (props.className ? props.className : '')
            }>
            <span className="popupmenu-title">
              {filteredGroups.length
                ? props.title
                : '移動するグループが存在しません'}
            </span>
            {props.closeElement}
            <ul className="popupmenu-list">
              {filteredGroups.map(group => {
                return (
                  <li
                    className="popupmenu-item cursor-pointer"
                    onClick={popupMenuItemClickHandler(group)}
                    key={group.id.id}>
                    <i
                      className={
                        groupToItemIconClass(group) + ' popupmenu-item-icon'
                      }></i>
                    <span className="popupmenu-item-text">
                      {groupToItemMessage(group)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          {props.backdropClickHandler && (
            <div
              className="popupmenu-backdrop"
              {...(props.backdropClickHandler
                ? { onClick: props.backdropClickHandler }
                : {})}
            />
          )}
        </>
      )}
    </>
  );
};

type PopupMenuProps = {
  groups: Group[];
  asset: Asset;
  changeGroup: (groupId: GroupId) => Promise<void>;
  showPopupMenu: boolean;
  closeHandlers: {
    onClose: () => void;
    shouldClosePopupMenuFromPoint: (event: React.MouseEvent) => boolean;
  };
  title: string;
};

export const PopupMenu: React.FC<PopupMenuProps> = props => {
  const onPopupMenuItemClick = async (groupId: GroupId): Promise<void> => {
    await props.changeGroup(groupId);
  };
  const closeElement = (
    <i
      className="icon-close popupmenu-close cursor-pointer"
      onClick={props.closeHandlers.onClose}></i>
  );

  return (
    <PopupMenuBase
      title={props.title}
      onPopupMenuItemClick={onPopupMenuItemClick}
      currentGroupId={props.asset.groupId}
      showPopupMenu={props.showPopupMenu}
      closeHandlers={props.closeHandlers}
      groups={props.groups}
      closeElement={closeElement}
    />
  );
};

type BulkPopupMenuProps = {
  groups: Group[];
  currentGroupId: GroupId;
  showPopupMenu: boolean;
  closeHandlers: {
    onClose: () => void;
    shouldClosePopupMenuFromPoint: (event: React.MouseEvent) => boolean;
  };
  onFinishedBulkOperation: React.MouseEventHandler;
  onPopupMenuItemClick: (groupId: GroupId) => Promise<void>;
};

export const BulkPopupMenu: React.FC<BulkPopupMenuProps> = props => {
  const closeElement = (
    <>
      <span
        className="popupmenu-finish"
        onClick={props.onFinishedBulkOperation}>
        一括操作を終了する
      </span>
      <i
        className="icon-close popupmenu-close cursor-pointer"
        onClick={(e): void => {
          e.stopPropagation();
          props.closeHandlers.onClose();
        }}></i>
    </>
  );

  const backdropClickHandler = (
    event: React.MouseEvent<HTMLDivElement>
  ): void => {
    const backdrop = event.currentTarget;

    event.stopPropagation();

    const prevBackdropDisplay = backdrop.style.display;
    backdrop.style.display = 'none';
    const shouldClose = props.closeHandlers.shouldClosePopupMenuFromPoint(
      event
    );
    if (shouldClose) {
      event.stopPropagation();
      props.closeHandlers.onClose();
    } else {
      const clickElement = document.elementFromPoint(
        event.clientX,
        event.clientY
      );
      (clickElement as HTMLElement)?.click();
      backdrop.style.display = prevBackdropDisplay;
    }
  };

  return (
    <PopupMenuBase
      title="選択した口座を"
      className="bulk"
      groups={props.groups}
      currentGroupId={props.currentGroupId}
      showPopupMenu={props.showPopupMenu}
      closeHandlers={props.closeHandlers}
      onPopupMenuItemClick={props.onPopupMenuItemClick}
      closeElement={closeElement}
      backdropClickHandler={backdropClickHandler}
    />
  );
};
